import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Divider, Form, Input, InputNumber, Switch } from "antd";
import type { Store } from "antd/es/form/interface";
import { debounce } from "lodash";
import { ReactNode, useEffect, useRef } from "react";

import {
  FormProps,
  isRangeType,
  SelectField,
  SelectFieldProps,
} from "../../form";
import { DateFilterInput } from "./DateFilterInput";

export interface FilterProps<T> extends Omit<SelectFieldProps, "title"> {
  title?: ReactNode;
  name: Extract<keyof T, string> | "divider";
  type:
    | "string"
    | "select"
    | "number"
    | "date"
    | "switch"
    | "custom"
    | "search"
    | "divider";
  multiple?: boolean;
  initialValue?: any;
  persistDefault?: boolean;
  hidden?: boolean;
  render?(): ReactNode | null;
}

interface FilterConfig<T> {
  entityName?: string;
  filters: FilterProps<T>[];
  formProps?: FormProps<any>;
  initialValues: T;
  scrollToFilter?: string;
  onChange(values: T): void;
}

const DEBOUNCE_DELAY = 600;

export function FilterPanel<T>({ formProps, ...props }: FilterConfig<T>) {
  const [form] = Form.useForm(formProps?.form);
  useEffect(() => {
    if (props.scrollToFilter)
      form.scrollToField(props.scrollToFilter, { block: "start", focus: true });
  }, [form, props.scrollToFilter]);

  const autoSubmit = useRef(
    debounce(() => form.submit(), DEBOUNCE_DELAY)
  ).current;

  useEffect(() => {
    return () => autoSubmit.cancel();
  }, []);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={props.initialValues as Store}
      onValuesChange={autoSubmit}
      onFinish={(values) => {
        props.filters.forEach((f) => {
          const val = values[f.name];
          if (
            !val ||
            (Array.isArray(val) && val.length === 0) ||
            (val.rangeType !== undefined && val.rangeType == null) ||
            (isRangeType(val.rangeType) && val.range == undefined)
          )
            delete values[f.name];
        });
        props.onChange(values as T);
      }}
    >
      {props.filters
        .filter((f) => !f.hidden)
        .map((filter) => {
          let input: ReactNode | null = null;
          switch (filter.type) {
            case "divider":
              return <Divider key={filter.name}>{filter.title}</Divider>;
            case "search":
            case "string":
              input = filter.multiple ? (
                <SelectField
                  mode="multiple"
                  onSearch={filter.onSearch}
                  options={filter.options}
                  showGroups={filter.showGroups}
                  optionsHook={filter.optionsHook}
                  optionsHookParams={filter.optionsHookParams}
                  optionsHookFilter={filter.optionsHookFilter}
                />
              ) : (
                <Input.Search name={filter.name} allowClear={true} />
              );
              break;
            case "select":
              input = (
                <SelectField
                  mode={filter.multiple ? "multiple" : undefined}
                  onSearch={filter.onSearch}
                  options={filter.options}
                  showGroups={filter.showGroups}
                  showSearch={filter.showSearch}
                  loading={filter.loading}
                  optionsHook={filter.optionsHook}
                  optionsHookParams={filter.optionsHookParams}
                  optionsHookFilter={filter.optionsHookFilter}
                  optionFilterProp={filter.optionFilterProp}
                />
              );
              break;
            case "date":
              input = <DateFilterInput />;
              break;
            case "switch":
              input = (
                <Switch
                  unCheckedChildren={<EyeInvisibleOutlined />}
                  checkedChildren={<EyeOutlined />}
                />
              );
              break;
            case "number":
              input = <InputNumber style={{ width: "100%" }} />;
              break;
            case "custom":
              if (filter.render) {
                input = filter.render();
              }
              break;
          }

          let label = filter.title;

          if (!label && filter.name === "name") label = props?.entityName;

          const itemName = filter.name as any;

          return (
            input && (
              <Form.Item
                key={filter.name}
                name={itemName}
                label={label}
                valuePropName={filter.type === "switch" ? "checked" : undefined}
                noStyle={!label}
              >
                {input}
              </Form.Item>
            )
          );
        })}
    </Form>
  );
}
